import type { ColumnDef, OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { Spec } from "@/types/specList";
import { GenericTable } from "./GenericTable";
import { TestLabelSpecsTableToolbar } from "../testlabels/TestLabelSpecsTableToolbar";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader } from "../ui/card";
import { InfoAlert } from "./Alerts";

type SpecsSelectTableProps = {
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelectionState?: RowSelectionState;
  specData: Spec[];
};

export default function SpecsSelectTable({
  onRowSelectionChange,
  rowSelectionState,
  specData,
}: SpecsSelectTableProps) {
  const deviceClasses = [...new Set(specData.map((s) => s.deviceClass))];
  const conditions = [...new Set(specData.map((sp) => sp.condition ?? "None"))];

  const columns: ColumnDef<Spec>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => {
            table.toggleAllRowsSelected(!!value);
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(checked) => {
            row.toggleSelected(!!checked);
          }}
          aria-label="Select row"
        />
      ),
      enableGlobalFilter: false,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: () => "Spec Name",
    },
    {
      accessorKey: "condition",
      header: () => "Condition",
      cell: ({ row }) => <Badge variant="outline">{row.original.condition}</Badge>,
      enableGlobalFilter: false,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "reportName",
      header: () => "Report Name",
    },
    {
      accessorKey: "legacyNames",
      header: () => "Legacy Name",
    },
    {
      accessorKey: "dutNames",
      header: () => "DUT Name",
    },
    {
      accessorKey: "deviceClass",
      header: () => "Device Class",
      cell: ({ row }) => <Badge variant="outline">{row.original.deviceClass}</Badge>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="font-bold pb-6">Add Specs</div>

        <InfoAlert>
          To link a specification to the Test Label, please select a specification by clicking the
          checkbox.
        </InfoAlert>
      </CardHeader>

      <CardContent>
        <GenericTable
          columns={columns}
          data={specData}
          onRowSelectionChange={onRowSelectionChange}
          rowSelection={rowSelectionState}
          toolbarComponent={(props) => (
            <TestLabelSpecsTableToolbar
              {...props}
              conditions={conditions}
              deviceClasses={deviceClasses}
            />
          )}
        />
      </CardContent>
    </Card>
  );
}
